import React from "react";
import { Link } from "react-router-dom";
import { Rocket, Users, TrendingUp, Network, Compass, HelpCircle, CheckCircle2 } from "lucide-react";
import XPlacement from "@/components/public/XPlacement";
import ComplianceDisclaimer from "@/components/public/ComplianceDisclaimer";
import PageMeta from "@/components/PageMeta";
import GrowthEngineSection from "@/components/public/GrowthEngineSection";
import { trackEvent } from "@/lib/retention";
import { useProgramTiers, rankRequirement } from "@/hooks/useProgramTiers";

const TIERS = [
  { num: "01", name: "Lift-Off", price: "$40/mo entry", note: "Your launchpad into the Lifted Network." },
  { num: "02", name: "Orbit",    price: "Earned via referrals", note: "Momentum compounds. Network expands." },
  { num: "03", name: "Galaxy",   price: "Earned via referrals", note: "Deeper access. Higher earning." },
  { num: "04", name: "Cosmos",   price: "Earned via referrals", note: "Long-term growth. Maximum rewards." },
];

const WHY_JOIN = [
  { icon: TrendingUp, t: "Real earnings", d: "Earn commissions on every Lifted purchase driven by your network." },
  { icon: Users,      t: "Real community", d: "Built for friends, families, and creators — not corporate solo sellers." },
  { icon: Network,    t: "No pay-to-win", d: "Tier-ups unlock through referrals, not bigger monthly fees." },
  { icon: Rocket,     t: "Whole ecosystem", d: "Earn across gear, accessories, supplies, and same-day delivery." },
];

const HOW_IT_WORKS = [
  { n: "01", t: "Join Lift-Off",    d: "Pick up the Lift-Off Tier on the Lifted storefront — $40/mo. That's it." },
  { n: "02", t: "Get your link",    d: "Auto-provisioned affiliate profile with QR code + tracking link." },
  { n: "03", t: "Share with people", d: "Post, text, hand out your QR — every conversion is attributed." },
  { n: "04", t: "Earn + level up",  d: "Commission ledger updates in real time. New referrals = next tier." },
];

const SUCCESS_PATH = [
  { stage: "Week 1",  d: "Sign up Lift-Off. Profile + link created instantly. Share with your inner circle." },
  { stage: "Month 1", d: "First commissions land. Grow to 10 active referrals → earn Orbit rank." },
  { stage: "Month 3", d: "Local network forms. Reach 100 active referrals → earn Galaxy rank with deeper rewards." },
  { stage: "Year 1",  d: "Leadership stage: 1,000 active referrals → Cosmos rank — max rewards + ecosystem governance opportunities." },
];

const FAQ = [
  { q: "Do I need experience selling?",
    a: "No. The Lift-Off tier exists so anyone can start without prior experience. Tools, training, and your QR/link are auto-provisioned." },
  { q: "How are tier-ups unlocked?",
    a: "Tiers unlock through referrals — not by paying more. Refer friends, family, or your community and your tier elevates automatically." },
  { q: "What can I earn from?",
    a: "Every transaction in the Lifted ecosystem your network drives: gear, accessories, supplies (delivery), and partner shops." },
  { q: "When do I get paid?",
    a: "Commissions accrue in your ledger and pay out on the standard ecosystem cycle (Stripe Connect payouts launch in Phase 11). Until then, balances are tracked and reconciled." },
  { q: "Can I be more than just an affiliate?",
    a: "Yes — affiliates often grow into drivers, partner shop operators, or franchisees. The roles compose (you keep affiliate income on top)." },
  { q: "Is this only US?",
    a: "Today: launched US-first. The ecosystem map is built to expand market by market — affiliate network has no geographic cap." },
];

export default function AffiliateZone() {
  const programTiers = useProgramTiers();
  return (
    <div data-testid="affiliate-zone" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <PageMeta title="Affiliate Zone" path="/affiliate" ogImage="/og-affiliate.png"
                description="Become a Lifted Affiliate. Earn 10–25% commission across clothing, accessories, supplies, and same-day delivery. No recruitment-only payouts." />
      {/* Hero */}
      <header className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-12">
        <div>
          <div className="eyebrow flex items-center gap-2"><Rocket className="h-3 w-3" /> Lifted Affiliate Zone</div>
          <h1 className="font-anton text-4xl sm:text-6xl uppercase mt-3 leading-[0.95]">Start where you are.<br /><span className="text-lifted-green">Grow at your pace.</span></h1>
          <p className="text-lifted-green font-bebas tracking-[0.18em] uppercase text-sm mt-3" data-testid="affiliate-outcome-promise">
            Affiliates help grow the Lifted ecosystem through content, referrals, and community awareness.
          </p>
          <p className="text-lifted-dim mt-5 max-w-lg">The LiftedAtTheMoment Affiliate Program is built for anyone who wants to grow — whether you're just getting started or already building something of your own. Join the Lifted Network and start at Lift-Off: a supportive, referral-based system designed to help you expand your reach, build real connections, and earn as your network grows.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/products/lift-off-tier" className="btn-lifted" data-testid="affiliate-zone-cta-join"
               onClick={() => trackEvent("affiliate_zone_join_click", { product: "lift-off-tier", surface: "affiliate_zone_hero" })}>Join Lift-Off Tier</Link>
            <Link to="/auth/register" className="btn-ghost" data-testid="affiliate-zone-cta-register">Create account first</Link>
          </div>
        </div>
        <div className="flex justify-center">
          <XPlacement zone="affiliate" variant="hero-tall" alt="Lifted Affiliate ambassador" />
        </div>
      </header>

      {/* Why Join */}
      <section data-testid="affiliate-why-join" className="mb-14">
        <div className="eyebrow !text-lifted-text-dim">Why join</div>
        <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-2 mb-6">Build with people, not algorithms.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {WHY_JOIN.map((b) => (
            <div key={b.t} data-testid={`affiliate-why-${b.t.toLowerCase().replace(/\s+/g,'-')}`} className="glass-panel p-5">
              <b.icon className="h-5 w-5 text-lifted-green" />
              <div className="font-anton uppercase mt-3 text-lg">{b.t}</div>
              <div className="text-xs text-lifted-dim mt-1.5">{b.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Growth Engine — ecosystem diagram + role connections */}
      <GrowthEngineSection id="zone-growth-engine" />
      <div id="content-creator" />

      {/* Tiers */}
      <section className="mb-14">
        <div className="eyebrow !text-lifted-text-dim">Tiers</div>
        <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-2 mb-2">Four tiers. One mission.</h2>
        <p className="text-lifted-dim text-sm max-w-2xl">No pay-to-level-up. Tiers unlock through referrals — pure community growth.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          {TIERS.map((t) => (
            <div key={t.name} data-testid={`affiliate-tier-${t.name.toLowerCase()}`} className="glass-panel p-6 hover:-translate-y-1 transition-transform">
              <div className="text-[0.6rem] text-lifted-mute font-bebas tracking-[0.18em]">Tier {t.num}</div>
              <div className="font-anton uppercase text-2xl text-lifted-green mt-2">{t.name}</div>
              <div className="text-xs text-lifted-text mt-1" data-testid={`tier-rank-req-${t.num}`}>
                {(() => { const pt = programTiers.find((x) => x.label === t.name); return pt ? `${rankRequirement(pt)} · ${pt.commission_pct}% commission` : t.price; })()}
              </div>
              {(() => { const pt = programTiers.find((x) => x.label === t.name); return pt ? (
                <div className="text-[11px] text-lifted-green mt-1" data-testid={`tier-box-price-${t.num}`}>
                  Optional {pt.box_name}: ${(pt.box_price_cents / 100).toFixed(0)}/mo (does not change rank)
                </div>) : null; })()}
              <p className="text-xs text-lifted-dim mt-3">{t.note}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section data-testid="affiliate-how-it-works" className="mb-14">
        <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><Compass className="h-3 w-3" /> How it works</div>
        <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-2 mb-6">Four steps. Real income.</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {HOW_IT_WORKS.map((s) => (
            <div key={s.n} data-testid={`affiliate-step-${s.n}`} className="glass-panel p-5">
              <div className="text-[0.6rem] text-lifted-mute font-bebas tracking-[0.18em] uppercase">Step {s.n}</div>
              <div className="font-anton uppercase mt-1 text-lg text-lifted-green">{s.t}</div>
              <div className="text-xs text-lifted-dim mt-2">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Earning Potential — compliance-safe rewrite (no dollar guarantees) */}
      <section data-testid="affiliate-earning-potential" className="mb-14 glass-panel p-6 sm:p-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          <div>
            <div className="eyebrow flex items-center gap-2"><TrendingUp className="h-3 w-3" /> What you can grow</div>
            <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-2">Tier benefits, not promises.</h2>
            <p className="text-lifted-dim text-sm mt-3 max-w-md">Each tier unlocks deeper rewards as your network grows. Tier progression is referral-based — never pay-to-win. Real earnings depend on real effort and network activity.</p>
          </div>
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { tier: "Lift-Off",  perks: ["Auto-provisioned affiliate link + QR", "Base commission on qualifying referrals", "Access to training after approval"] },
              { tier: "Orbit",     perks: ["Unlocked via referrals (no upgrade fee)", "Higher commission tier on qualifying sales", "Network analytics in your dashboard"] },
              { tier: "Galaxy",    perks: ["Deeper commission tier", "Cross-module earnings (gear + supplies + partner shops)", "Priority support + early-drop notifications"] },
              { tier: "Cosmos",    perks: ["Leadership stage — top commission tier", "Multi-region opportunities", "Ecosystem governance participation"] },
            ].map((row) => (
              <div key={row.tier} data-testid={`affiliate-earning-${row.tier.toLowerCase()}`} className="glass-panel p-4">
                <div className="font-anton uppercase text-lg text-lifted-green">{row.tier}</div>
                <ul className="mt-2 space-y-1.5">
                  {row.perks.map((p) => (
                    <li key={p} className="text-[0.72rem] text-lifted-text leading-snug flex gap-2">
                      <CheckCircle2 className="h-3 w-3 text-lifted-green shrink-0 mt-0.5" aria-hidden="true" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <ComplianceDisclaimer variant="block" testIdSuffix="zone-earnings" />
      </section>

      {/* Success Path */}
      <section data-testid="affiliate-success-path" className="mb-14">
        <div className="eyebrow !text-lifted-text-dim">Success path</div>
        <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-2 mb-6">A realistic timeline.</h2>
        <ol className="relative border-l border-white/10 pl-6 space-y-5">
          {SUCCESS_PATH.map((s, i) => (
            <li key={s.stage} data-testid={`affiliate-path-${i}`} className="relative">
              <span className="absolute -left-[34px] top-1.5 w-4 h-4 rounded-full bg-lifted-green/30 border-2 border-lifted-green grid place-items-center">
                <CheckCircle2 className="h-2.5 w-2.5 text-lifted-green" />
              </span>
              <div className="font-anton uppercase text-lifted-green text-base">{s.stage}</div>
              <div className="text-sm text-lifted-dim mt-1">{s.d}</div>
            </li>
          ))}
        </ol>
      </section>

      {/* FAQ */}
      <section data-testid="affiliate-faq" className="mb-14">
        <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><HelpCircle className="h-3 w-3" /> FAQ</div>
        <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-2 mb-6">Questions answered.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FAQ.map((f, i) => (
            <details key={i} data-testid={`affiliate-faq-${i}`} className="glass-panel p-5 group">
              <summary className="cursor-pointer font-anton uppercase text-base text-lifted-text list-none flex items-start justify-between gap-3">
                <span>{f.q}</span>
                <span className="text-lifted-green group-open:rotate-45 transition-transform inline-block">+</span>
              </summary>
              <p className="text-sm text-lifted-dim mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Closer */}
      <section className="text-center max-w-2xl mx-auto" data-testid="affiliate-zone-closer">
        <h3 className="font-anton text-2xl sm:text-3xl uppercase">This isn't just about selling.</h3>
        <p className="text-lifted-dim mt-3">It's about creating pathways, sharing opportunities, and expanding something bigger than yourself. Welcome to the Lifted Network.</p>
        <div className="mt-6 flex justify-center gap-3 flex-wrap">
          <Link to="/products/lift-off-tier" className="btn-lifted" data-testid="affiliate-zone-cta-bottom">Buy Lift-Off Tier</Link>
          <Link to="/about" className="btn-ghost">Learn about LiftedAtTheMoment</Link>
        </div>
      </section>
    </div>
  );
}
